<template>
  <div class="form">
    <div class="vcore_row">
      <div class="vcore_col--xs--12" v-if="form.name">
        <h3>{{ form.name }}</h3>
      </div>
      <div v-for="field in form.fields" :class="[...getSizeClases(field.size), 'field']" v-if="!hideIf(field)">
        <component :is="field.type" :label="field.name" :placeholder="field.placeholder" v-model="data[field.value]"
          :options="field.options" :mask-options="getMaskOptions(field.mask)"
          :required="getRequired($v.data[field.value])" :error="getError($v.data[field.value], field)"
          :validStatus="!$v.data[field.value].$invalid" :triggers="field.triggers || {}"
          @blur="$v.data[field.value].$touch()" :form-name="form.name" :popover="field.popover" :trim="field.trim"
          :hint="field.hint" :datepicker="field.datepicker" v-bind="field.componentProps"
          :validate-on="field.validateOn" :disabled="field.disabled" :guidanceText="field.guidanceText"
          :inputmode="field.inputmode" :pattern="field.pattern" :autocomplete="field.autocomplete"/>
      </div>
    </div>
  </div>
</template>

<script>
import { isEqual, template } from "lodash";
import * as validators from "vuelidate/lib/validators";
import { EventBus } from "../utils/event-bus";
import Input from "./Input";
import Select from "./Select";
import Money from "./Money";
import Ssn from "./Ssn";
import Radio from "./Radio";
import Dateselector from "./Dateselector";
import popover from "./Popover";
import Masks from "../utils/Masks";
import customValidators from "../utils/Validators";
import { resolvePropertyByString } from "../utils/Object";

export default {
  name: "CustomForm",
  components: {
    Input,
    Money,
    Select,
    Ssn,
    Dateselector,
    Radio
  },
  props: {
    value: Object,
    form: Object,
    validateOnCreate: {
      type: Boolean,
      default: false
    },

    skipValidation: {
      type: Boolean,
      default: false
    },

  },
  data() {
    return {
      defaultField: {
        name: "Field",
        size: {
          xs: "12",
          md: "4"
        },
        type: "Input",
        placeholder: "",
        choices: [],
        required: true
      },
      masks: Masks,
      eventBus: EventBus,
      data: {
        ...this.defaultValue(),
        ...this.staticValues(),
        ...this.value
      }
    };
  },
  validations() {
    return {
      data: {
        ...this.form.fields.reduce((acc, cur) => {
          return {
            ...acc,
            [cur.value]: this.generateVuelidateObj(cur.validation)
          };
        }, {})
      }
    };
  },
  watch: {
    data: {
      handler(newVal, oldVal) {
        // TODO: Only emit if values from it's form has changed. Not other CustomForms using same binding
        this.$emit("input", this.data);
      },
      deep: true,
      immediate: true
    },
    value: {
      handler() {
        if (!isEqual(this.data, this.value)) {
          this.data = {
            ...this.data,
            ...this.value
          };
        }
      },
      deep: true
    },
    $v: {
      // Emit changes of validation object so parents can see
      handler() {
        this.$emit("valid-change", this.$v);
      },
      deep: true,
      immediate: true
    },
    errors() {
      this.$emit("errors-change", this.errors);
    }
  },
  computed: {
    errors() {
      return this.form.fields
        .map(x => this.getError(this.$v.data[x.value], x))
        .filter(x => x !== "");
    },
    console: () => console
  },
  methods: {
    getSizeClases(sizeObj) {
      //janky way to add blank of size 8 and the right height
      if(sizeObj == "hideRest"){
        const hide = [`vcore_col--xs--12 hide`];
        return hide;
      }
      return Object.keys(sizeObj).map(x => `vcore_col--${x}--${sizeObj[x]}`);
    },
    defaultValue() {
      return this.form.fields.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.value]: cur.defaultValue || ""
        }),
        {}
      );
    },
    staticValues() {
      return this.form.static || {};
    },
    touch() {
      this.$v.$touch();
    },
    reset() {
      this.$v.$reset();
    },
    getMaskOptions(mask) {
      if (mask) {
        if (typeof mask === "string") {
          return {
            mask: this.masks[mask](),
            guide: false
          };
        }

        return {
          mask: this.masks[mask.type](mask.options),
          guide: false
        };
      }

      return;
    },
    getError(validation, field) {
      if (!validation.$error) {
        return "";
      }

      if (typeof field.error === "string") {
        return field.error;
      }

      // Determine which error to show based off of validations
      const customErrors = Object.keys(field.error);

      // Map each failing validation with the error it should show.
      const validations = Object.keys(validation.$params)
        .filter(x => !validation[x])
        .map(x => (customErrors.indexOf(x) !== -1 ? x : "default"));

      // Determine which error should show based on order
      const error = field.errorOrder.filter(
        x => validations.indexOf(x) !== -1
      )[0];

      const errVal = field.error[error];

      // Format error if an object
      if (typeof errVal !== "string") {
        return this.formatString(errVal.template, errVal.args.map(x => {
          if (typeof x === "string") {
            return resolvePropertyByString(this.$store.state, x);
          }

          let value = resolvePropertyByString(this.$store.state, x.value);

          return !!x.filter ? this.$options.filters[x.filter](value) : value;
        }));
      }

      return errVal;
    },
    hideIf(field) {
      if (!field.hideIf || typeof field.hideIf !== "function") {
        return false;
      }
      return field.hideIf(this.value || {});
    },
    getRequired(validation) {
      let required = false;

      if (validation.$params.hasOwnProperty("requiredIf")) {
        required = required || validation.$params.requiredIf.prop();
      }

      if (validation.$params.hasOwnProperty("required")) {
        required = true;
      }

      return required;
    },
    generateVuelidateObj(rules) {
      // Generate Vuelidate rules from the "validation" object in form
      return Object.entries(rules).reduce((acc, [key, val]) => {
        let obj = { ...acc };

        switch (key) {
          case "alpha":
            if (val) {
              obj.alpha = validators.alpha;
            }
            break;
          case "numeric":
            if (val) {
              obj.numeric = validators.numeric;
            }
            break;
          case "email":
            if (val) {
              obj.email = validators.email;
            }
            break;
          case "date":
            if (val) {
              obj.date = customValidators.date;
            }
            break;
          case "expDate":
            if (val) {
              obj.expDate = customValidators.expDate;
            }
            break;
          case "phone":
            if (val) {
              obj.phone = customValidators.phone;
            }
            break;
          case "zip":
            if (val) {
              obj.zip = customValidators.zip;
            }
            break;
          case "minimumAge":
            obj.minimumAge = customValidators.minimumAge(val);
            break;
          case "required":
            if (val) {
              obj.required = validators.required;
            }
            break;
          case "minMoney":
            if (val) {
              obj.minMoney = customValidators.minMoney(val, this.$store);
            }
            break;
          case "maxMoney":
            if (val) {
              obj.maxMoney = customValidators.maxMoney(val, this.$store);
            }
            break;
          case "requiredIf":
            obj.requiredIf = validators.requiredIf(() => {
              if (typeof val !== "function") {
                return false;
              }

              return val(this.value || {});
            });
            break;
          case "minimum":
            obj.minValue = validators.minValue(val);
            break;
          case "maximum":
            obj.maxValue = validators.maxValue(val);
            break;
          case "minLength":
            obj.minLength = validators.minLength(val);
            break;
          case "maxLength":
            obj.maxLength = validators.maxLength(val);
            break;
          case "between":
            const [min, max] = val;
            obj.between = validators.between(min, max);
            break;
          case "sameAs":
            obj.sameAs = validators.sameAs(val);
            break;
          case "poBox":
            if (val) {
              obj.poBox = customValidators.poBox;
            }
            break;
          case "noPoBox":
            if (val) {
              obj.noPoBox = customValidators.noPoBox;
            }
            break;
          case "houseNumber":
            if (val) {
              obj.houseNumber = customValidators.houseNumber;
            }
            break;
          case "regex":
            if (val) {
              obj.regex = customValidators.regex(val, this.$store);
            }
            break;
          case "ssn":
            if (val) {
              obj.ssn = customValidators.ssn;
            }
            break;
          case "ssnPopulated":
            if (val) {
              obj.ssnPopulated = customValidators.ssnPopulated(this.$store);
            }
            break;
          case "minNumLength":
            if (val) {
              obj.minNumLength = customValidators.minNumLength(val);
            }
            break;
          case "maxNumLength":
            if (val) {
              obj.maxNumLength = customValidators.maxNumLength(val);
            }
            break;
          case "inDateRange":
            if (val) {
              obj.inDateRange = customValidators.inDateRange(val);
            }
            break;
          case "creditOrDebitCard":
            if (val) {
              obj.creditOrDebitCard = customValidators.creditOrDebitCard(this.$store);
            }
            break;
          case "bankNamePopulated":
            if (val) {
              obj.bankNamePopulated = customValidators.bankNamePopulated(this.$store);
            }
            break;
          case "fullNameLimit":
            if (val) {
              obj.fullNameLimit = customValidators.fullNameLimit(val, this.$store);
            }
            break;
          case "alphaWithDash":
            if (val) {
              obj.alphaWithDash = customValidators.alphaWithDash(val);
            }
            break;
          case "excludeTsysAccountId":
            if (val) {
              obj.excludeTsysAccountId = customValidators.excludeTsysAccountId;
            }
            break;
        }

        return obj;
      }, {});
    },
    formatString(s, args) {
      var formatted = s;

      for (var i = 0; i < args.length; i++) {
        var regexp = new RegExp('\\{' + i + '\\}', 'gi');
        if (args[i].substring(0, 1) === "$") {
          args[i] = "$" + args[i];
        }
        formatted = formatted.replace(regexp, args[i]);
      }

      return formatted;
    }
  },
  mounted() {
    this.eventBus.$on("validateFields", (formName) => {
      this.touch();
      //need to only get the errors from the current form
      if (this.form.name == formName) {
        let temp = this.form.fields
          .map(x => this.getError(this.$v.data[x.value], x))
          .filter(x => x !== "");
        this.eventBus.$emit("setErrorList", temp);
      }
    });
  },
  created() {
    if (this.validateOnCreate) {
      //check for errors on all auto-poulated entries
      this.touch();
    }


  },
  destroyed() {
    this.eventBus.$off("validateFields", () => { });
  },
};
</script>
<style lang="scss">
#mi.vcore_input {
  @media (max-width: $grid-breakpoint-sm) {
    width: 30%;
  }
}
</style>

<style lang="scss" scoped>
.form {
  margin-top: 24px;
}

.field {
  margin: 10px 0 13px;
}

h3 {
  font-family: Poppins;
}

.hide {
  width: 100%;
  height: 0px;
  margin-top: 0px !important;
  margin-bottom: 0px !important;
  @media(max-width: $grid-breakpoint-sm) {
    //don't need a spacer on mobile
    display: none;
  }
}

@media(max-width: $grid-breakpoint-sm) {
  .basic-info-pg {
    .vcore_col--xs--5 {
      width: 100%;
    }

    .vcore_col--xs--2 {
      width: 100%;
    }
  }
}
</style>
